"use client";

import { useEffect, useMemo, useState, ReactNode } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

type Tab = {
  id: string;
  label: string;
  content: ReactNode;
};

type TabsProps = {
  tabs: Tab[];
  defaultTab?: string;
  /**
   * Якщо true — активний таб береться/записується в URL query (shareable + refresh-safe).
   */
  syncWithUrl?: boolean;
  /**
   * Назва query-параметра для табу (дефолт: "tab")
   */
  queryKey?: string;
  /**
   * Якщо true — використовує replace замість push (за замовчуванням true, щоб не засмічувати історію)
   */
  replaceHistory?: boolean;
  /**
   * Дозволяє "очистити" або модифікувати query при зміні табу
   * (наприклад прибрати page/sort/pageSize, щоб вони не тягнулися між табами).
   */
  transformSearchParams?: (
    params: URLSearchParams,
    nextTabId: string
  ) => URLSearchParams;
  /** Fired on a real tab switch (re-clicking the active tab is a no-op). */
  onTabChange?: (tabId: string) => void;
};

function TabsBase({
  tabs,
  defaultTab,
  onTabChange,
}: {
  tabs: Tab[];
  defaultTab?: string;
  onTabChange?: (tabId: string) => void;
}) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id || "");

  const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content;

  if (tabs.length === 0) return null;

  return (
    <div className="w-full">
      {/* Tab Buttons */}
      <div
        className="flex flex-nowrap md:flex-wrap gap-2 mb-6 overflow-x-auto pb-1"
        style={{
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
          scrollbarColor: "#3a4a5a transparent",
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => {
                // Re-clicking the active tab must be a no-op — onTabChange
                // call-sites strip params (page/sort) on switch, so a stray
                // click would wipe the user's pagination state.
                if (tab.id === activeTab) return;
                setActiveTab(tab.id);
                onTabChange?.(tab.id);
              }}
              className={`
                px-4 py-2.5 text-sm font-medium transition-all relative
                whitespace-nowrap rounded-t-lg border-t border-x flex-shrink-0
                ${
                  isActive
                    ? "bg-[#1f2e3f] border-[#2a475e] border-b-[#1f2e3f] text-[#66c0f4] shadow-sm -mb-px z-10"
                    : "bg-[#16202d] border-[#2a475e] text-[#c7d5e0] hover:bg-[#1f2e3f] hover:text-white hover:border-[#3a556e]"
                }
              `}
            >
              {tab.label}
              {/* Active indicator */}
              {isActive && (
                <span
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#66c0f4]"
                  style={{
                    boxShadow: "0 0 8px rgba(102, 192, 244, 0.6)",
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="w-full">{activeTabContent}</div>
    </div>
  );
}

function TabsWithUrlSync({
  tabs,
  defaultTab,
  queryKey = "tab",
  replaceHistory = true,
  transformSearchParams,
  onTabChange,
}: Required<Pick<TabsProps, "tabs">> &
  Pick<
    TabsProps,
    | "defaultTab"
    | "queryKey"
    | "replaceHistory"
    | "transformSearchParams"
    | "onTabChange"
  >) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const urlTab = searchParams.get(queryKey ?? "tab");
  const normalizedDefault = defaultTab || tabs[0]?.id || "";

  const initialTab = useMemo(() => {
    if (!tabs.length) return "";
    if (urlTab && tabs.some((t) => t.id === urlTab)) {
      return urlTab;
    }
    if (tabs.some((t) => t.id === normalizedDefault)) {
      return normalizedDefault;
    }
    return tabs[0].id;
  }, [tabs, urlTab, normalizedDefault]);

  // Keep internal activeTab in sync with URL (but render via TabsBase)
  // We do it by setting defaultTab=initialTab and updating URL on clicks.
  useEffect(() => {
    // Normalize only a PRESENT-but-invalid tab param. An absent param needs
    // no URL write — defaultTab already renders correctly, and the write was
    // a real RSC navigation on every visit (it could also clobber an early
    // history.pushState pagination click back to page 1).
    if (!tabs.length) return;
    if (!urlTab) return;
    if (tabs.some((t) => t.id === urlTab)) return;

    const sp = new URLSearchParams(searchParams.toString());
    sp.set(queryKey ?? "tab", initialTab);
    router.replace(`${pathname}?${sp.toString()}`, { scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, urlTab, initialTab]);

  const updateUrl = (tabId: string) => {
    onTabChange?.(tabId);
    let sp = new URLSearchParams(searchParams.toString());
    if (transformSearchParams) {
      sp = transformSearchParams(sp, tabId);
    }
    sp.set(queryKey ?? "tab", tabId);
    const nextUrl = `${pathname}?${sp.toString()}`;
    if (replaceHistory) {
      router.replace(nextUrl, { scroll: false });
    } else {
      router.push(nextUrl, { scroll: false });
    }
  };

  return (
    <TabsBase tabs={tabs} defaultTab={initialTab} onTabChange={updateUrl} />
  );
}

export function Tabs({
  tabs,
  defaultTab,
  syncWithUrl = false,
  queryKey = "tab",
  replaceHistory = true,
  transformSearchParams,
  onTabChange,
}: TabsProps) {
  // Важливо: useSearchParams викликаємо ТІЛЬКИ в TabsWithUrlSync,
  // щоб не ламати prerender сторінок, які просто використовують Tabs.
  if (syncWithUrl) {
    return (
      <TabsWithUrlSync
        tabs={tabs}
        defaultTab={defaultTab}
        queryKey={queryKey}
        replaceHistory={replaceHistory}
        transformSearchParams={transformSearchParams}
        onTabChange={onTabChange}
      />
    );
  }

  return (
    <TabsBase tabs={tabs} defaultTab={defaultTab} onTabChange={onTabChange} />
  );
}
